import { useEffect, useState } from 'react';
import Reveal from './Reveal';
import ligue1Logo from '../assets/league/Ligue_1.png';
import laLigaLogo from '../assets/league/LaLiga.png';
import uclLogo from '../assets/league/ucl.png';
import championsPoster from '../assets/sports/championsleague02.jpg';
import { useLanguage } from '../i18n/LanguageContext';
import { footballPromoContent } from '../i18n/content/footballPromo';

const SHIELD_ICON = (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2 4 5v6c0 5 3.5 8.5 8 11 4.5-2.5 8-6 8-11V5z"></path>
    </svg>
);

const GIFT_ICON = (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="8" width="18" height="4"></rect>
        <path d="M12 8v13M19 12v7a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-7"></path>
        <path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5"></path>
    </svg>
);

const LOCK_ICON = (
    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="11" width="16" height="9" rx="2"></rect>
        <path d="M8 11V7a4 4 0 0 1 8 0v4"></path>
    </svg>
);

const FEATURE_ICONS = [
    SHIELD_ICON,
    (
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="4" width="20" height="13" rx="2"></rect>
            <path d="M8 21h8M12 17v4"></path>
        </svg>
    ),
    (
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path fill="currentColor" d="M7 2v11h3v9l7-12h-4l3-8z"></path></svg>
    ),
];

// Countdown resets every day at midnight — keeps the "flash offer" feel alive
// without a fake far-future date.
function getMsUntilMidnight() {
    const now = new Date();
    const next = new Date(now);
    next.setHours(24, 0, 0, 0);
    return next.getTime() - now.getTime();
}

const pad2 = (n) => String(n).padStart(2, '0');

export default function FootballPromoBanner() {
    const { lang } = useLanguage();
    const c = footballPromoContent[lang];
    const features = c.features.map((label, i) => ({ label, icon: FEATURE_ICONS[i] }));
    const leagueLogo = lang === 'es' ? laLigaLogo : ligue1Logo;
    const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });

    useEffect(() => {
        const tick = () => {
            const ms = getMsUntilMidnight();
            setTimeLeft({
                hours: Math.floor(ms / 3600000),
                minutes: Math.floor((ms % 3600000) / 60000),
                seconds: Math.floor((ms % 60000) / 1000),
            });
        };
        tick();
        const timer = setInterval(tick, 1000);
        return () => clearInterval(timer);
    }, []);

    return (
        <Reveal className="promo-banner relative overflow-hidden rounded-3xl">
            <div className="absolute inset-0 promo-banner-glow" aria-hidden="true"></div>
            <div className="absolute inset-0 promo-banner-dots" aria-hidden="true"></div>

            <div className="relative z-10 grid lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-8 items-center p-6 sm:p-10 lg:p-14">
                <div className="flex flex-col gap-5 order-2 lg:order-1">
                    <span className="promo-badge">
                        {SHIELD_ICON}
                        {c.badge}
                    </span>

                    <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold text-white leading-[1.1] tracking-tight">
                        {c.titleLine1}
                        <br />
                        <span className="promo-accent-text">{c.titleAccent}</span>
                    </h2>

                    <p className="text-white/70 text-sm sm:text-base max-w-lg leading-relaxed">
                        {c.body}
                    </p>

                    <div className="flex flex-wrap gap-3">
                        {features.map((f) => (
                            <span key={f.label} className="promo-pill">
                                {f.icon}
                                {f.label}
                            </span>
                        ))}
                    </div>

                    <div className="flex flex-wrap items-center gap-4 mt-1">
                        <a href="#services" className="promo-cta">
                            {GIFT_ICON}
                            {c.cta}
                        </a>
                        <span className="inline-flex items-center gap-1.5 text-white/50 text-xs font-medium">
                            {LOCK_ICON}
                            {c.security}
                        </span>
                    </div>
                </div>

                <div className="flex flex-col items-center gap-8 order-1 lg:order-2">
                    <div className="relative w-full max-w-[300px] h-[210px] sm:h-[230px]">
                        <div className="promo-card promo-card-side promo-card-left">
                            <img src={leagueLogo} alt={c.leagueLogoAlt} className="promo-card-logo-crop" loading="lazy" />
                        </div>
                        <div className="promo-card promo-card-main">
                            <img src={championsPoster} alt="UEFA Champions League" loading="lazy" />
                        </div>
                        <div className="promo-card promo-card-side promo-card-right">
                            <img src={uclLogo} alt="UEFA Champions League" className="p-4 object-contain" loading="lazy" />
                        </div>
                    </div>

                    <div className="promo-countdown">
                        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-secondary-bright mb-3 text-center">
                            {c.offerExpires}
                        </p>
                        <div className="flex gap-2">
                            <div className="promo-countdown-box">
                                <span>{pad2(timeLeft.hours)}</span>
                                <small>{c.hours}</small>
                            </div>
                            <div className="promo-countdown-box">
                                <span>{pad2(timeLeft.minutes)}</span>
                                <small>{c.minutes}</small>
                            </div>
                            <div className="promo-countdown-box">
                                <span>{pad2(timeLeft.seconds)}</span>
                                <small>{c.seconds}</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Reveal>
    );
}
